import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, Briefcase, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import JobCard from "@/components/site/JobCard";
import PageHero from "@/components/site/PageHero";
import Pager from "@/components/site/Pager";
import Reveal from "@/components/site/Reveal";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const DEPTS_FALLBACK = ["R&D", "Quality", "Clinical", "Regulatory", "Manufacturing", "Sales & Marketing", "Supply Chain", "Medical Affairs"];
const TYPES = ["Full-time", "Contract", "Internship", "Part-time"];
const PAGE_SIZE = 12;

export default function Jobs() {
  useSEO({
    title: "Current Openings",
    description: "Browse current pharma openings — formulation, QA, clinical, regulatory, manufacturing, sales and more.",
    pageKey: "jobs",
  });
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [location, setLocation] = useState(params.get("location") || "");
  const [department, setDepartment] = useState(params.get("department") || "");
  const [subDepartment, setSubDepartment] = useState(params.get("sub_department") || "");
  const [employmentType, setEmploymentType] = useState(params.get("employment_type") || "");
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10) || 1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deptDocs, setDeptDocs] = useState([]);

  useEffect(() => {
    api.get("/departments")
      .then((r) => { setDeptDocs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
  }, []);

  const parentDepts = deptDocs.filter((d) => !d.parent_id);
  const parentNames = parentDepts.length ? parentDepts.map((d) => d.name) : DEPTS_FALLBACK;
  const selectedParent = deptDocs.find((d) => d.name === department);
  const subs = selectedParent ? deptDocs.filter((d) => d.parent_id === selectedParent.id) : [];

  const queryParams = useMemo(
    () => ({
      q: q || undefined,
      location: location || undefined,
      department: department || undefined,
      sub_department: subDepartment || undefined,
      employment_type: employmentType || undefined,
      page,
      page_size: PAGE_SIZE,
    }),
    [q, location, department, subDepartment, employmentType, page],
  );

  useEffect(() => {
    setLoading(true);
    api.get("/jobs", { params: queryParams }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  }, [queryParams]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [q, location, department, subDepartment, employmentType]);
  // When parent dept changes, clear sub-dept
  useEffect(() => { setSubDepartment(""); }, [department]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (location) next.set("location", location);
    if (department) next.set("department", department);
    if (subDepartment) next.set("sub_department", subDepartment);
    if (employmentType) next.set("employment_type", employmentType);
    if (page > 1) next.set("page", String(page));
    setParams(next, { replace: true });
  }, [q, location, department, subDepartment, employmentType, page, setParams]);

  const clear = () => { setQ(""); setLocation(""); setDepartment(""); setSubDepartment(""); setEmploymentType(""); setPage(1); };

  return (
    <>
      <PageHero
        overline="Current Openings"
        title="Find your next role in pharma."
        description="Active roles across formulation, regulatory, clinical, QA, manufacturing and commercial. New openings added every week."
      />
      <section className="border-b border-slate-200 bg-white">
        <div className="container-page py-6">
          <div className="grid gap-3 md:grid-cols-12" data-testid="jobs-filters">
            <div className="relative md:col-span-5">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, skill, keyword…"
                data-testid="jobs-search-input"
                className="pl-9"
              />
            </div>
            <div className="relative md:col-span-3">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Location (e.g. Mumbai)"
                data-testid="jobs-location-input"
                className="pl-9"
              />
            </div>
            <div className="md:col-span-2">
              <Select value={department || "all"} onValueChange={(v) => setDepartment(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="jobs-department-select"><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {parentNames.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {subs.length > 0 && (
              <div className="md:col-span-2">
                <Select value={subDepartment || "all"} onValueChange={(v) => setSubDepartment(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="jobs-sub-department-select"><SelectValue placeholder="Sub-department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sub-departments</SelectItem>
                    {subs.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={subs.length > 0 ? "md:col-span-2" : "md:col-span-2"}>
              <Select value={employmentType || "all"} onValueChange={(v) => setEmploymentType(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="jobs-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(q || location || department || employmentType) && (
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider text-slate-500">Active filters</span>
              <Button variant="ghost" size="sm" onClick={clear} data-testid="jobs-clear-filters" className="h-7 gap-1 text-xs">
                <X className="h-3 w-3" /> Clear all
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="section-pad bg-slate-50">
        <div className="container-page">
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              <Briefcase className="mr-2 inline h-5 w-5 text-primary" />
              {loading ? "Loading…" : `${total} role${total === 1 ? "" : "s"} found`}
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="jobs-grid">
            {items.map((job, i) => (
              <Reveal key={job.id} variant="up" delay={Math.min(i * 60, 300)}>
                <JobCard job={job} />
              </Reveal>
            ))}
          </div>
          {!loading && items.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
              No matching openings. Try fewer filters or upload your resume — we'll reach out.
            </div>
          )}
          <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="jobs-pager" />
        </div>
      </section>
    </>
  );
}
